import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';

const { FiArrowRight, FiAward, FiTool, FiClock, FiMapPin, FiShield, FiUsers, FiZap } = FiIcons;

const Home = () => {
  const { getModelData } = useInventory();
  const modelData = getModelData();
  const featuredModels = modelData.slice(0, 3);

  const audiAdvantages = [
    {
      icon: FiZap,
      title: 'quattro All-Wheel Drive',
      description: 'Legendary traction and handling in all conditions, perfect for Maritime weather and Nova Scotia roads'
    },
    {
      icon: FiAward,
      title: 'Audi Virtual Cockpit',
      description: 'Fully digital 12.3" instrument cluster with customizable displays and intuitive controls'
    },
    {
      icon: FiTool,
      title: 'Factory-Trained Service',
      description: 'Certified Audi technicians using genuine parts and specialized diagnostic equipment'
    },
    {
      icon: FiUsers,
      title: 'Audi Brand Specialists',
      description: 'Expert knowledge of Audi technology, features, and the perfect model for your lifestyle'
    }
  ];

  const audiTechnologies = [
    { name: 'quattro AWD', description: 'Intelligent all-wheel drive for superior traction and performance', icon: '🔄' },
    { name: 'Virtual Cockpit', description: 'Customizable digital instrument cluster', icon: '📱' },
    { name: 'MMI Touch Response', description: 'Intuitive infotainment with haptic feedback', icon: '👆' },
    { name: 'Bang & Olufsen', description: 'Premium 3D sound system', icon: '🎵' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1617469767053-d3b16ee6a4ab?w=1920&h=1080&fit=crop&crop=bottom"
            alt="Audi e-tron GT"
            className="w-full h-full object-cover opacity-70"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center md:text-left md:max-w-2xl"
          >
            <h1 className="text-4xl md:text-6xl font-light mb-6 uppercase tracking-tight">
              Vorsprung durch Technik
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl font-light">
              Experience the pinnacle of German engineering and luxury at Halifax Audi. Discover our complete lineup of premium vehicles featuring quattro AWD, advanced technology, and uncompromising performance.
            </p>
            <div className="flex items-center justify-center md:justify-start space-x-2 mb-8 text-gray-300">
              <SafeIcon icon={FiMapPin} className="w-5 h-5" />
              <span>6077 Columbus St, Halifax, NS B3K 0E9 • (902) 455-7890</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link to="/models" className="btn-audi-primary">
                <span>Explore Audi Models</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2 inline-block" />
              </Link>
              <Link to="/build-your-audi" className="btn-audi-outline">
                Build Your Own Audi
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Audi Brand Promise */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Progress Through Technology
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              For over 50 years, Audi has pioneered automotive innovation. From the legendary quattro all-wheel drive system to today's electric e-tron models, we continue to push the boundaries of what's possible in luxury automotive engineering.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {audiTechnologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{tech.icon}</div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {tech.name}
                </h3>
                <p className="text-gray-600">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Audi Models */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Discover Our Audi Lineup
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From the sporty A4 sedan to the versatile Q7 SUV, each Audi model combines sophisticated design with cutting-edge technology and quattro performance.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredModels.map((model, index) => (
              <motion.div
                key={model.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative">
                  <img src={model.image} alt={`Audi ${model.name}`} className="w-full h-60 object-cover" />
                  <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-3 py-1 text-sm font-medium uppercase tracking-wider">
                    {model.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                    Audi {model.name}
                  </h3>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-red-600">
                      Starting at ${model.startingPrice.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6 line-clamp-2">{model.description}</p>
                  {/* Audi Technology Highlights */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-2 uppercase tracking-wider">
                      Key Technologies
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {model.audiTechnology?.slice(0, 2).map((tech, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 text-xs">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Link
                      to={`/models/${model.id}`}
                      className="block w-full bg-black text-white py-3 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
                    >
                      Explore {model.name}
                    </Link>
                    <Link
                      to={`/build-your-audi/${model.id}`}
                      className="block w-full bg-red-600 text-white py-3 px-4 font-medium hover:bg-red-700 transition-colors text-center uppercase tracking-wider text-sm"
                    >
                      Build Your Own
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/models" className="bg-black text-white px-8 py-3 font-medium hover:bg-gray-900 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm">
              <span>View All Audi Models</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* quattro AWD Feature Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img src="https://images.unsplash.com/photo-1600706432309-817cb3872349?w=800" alt="Audi quattro AWD" className="shadow-xl" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Legendary quattro AWD
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                For over 40 years, Audi's quattro all-wheel drive has set the standard for traction and performance. Perfect for Halifax winters and Nova Scotia's varied road conditions, quattro intelligently distributes power for optimal grip and handling in any weather.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">1</span>
                  </div>
                  <p className="text-gray-700">Intelligent power distribution to all four wheels</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">2</span>
                  </div>
                  <p className="text-gray-700">Superior traction in snow, rain, and dry conditions</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">3</span>
                  </div>
                  <p className="text-gray-700">Enhanced cornering stability and confidence</p>
                </div>
              </div>
              <Link to="/build-your-audi" className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm">
                <span>Build Your Audi</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Halifax Audi */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              The Halifax Audi Advantage
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of Nova Scotia's premier Audi dealership, where passionate expertise meets exceptional service.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {audiAdvantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100"
              >
                <div className="bg-red-600 p-3 rounded-full w-fit mb-4">
                  <SafeIcon icon={advantage.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {advantage.title}
                </h3>
                <p className="text-gray-600">
                  {advantage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audi Service Excellence */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-6 uppercase tracking-tight">
                Audi Service Excellence
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Keep your Audi performing at its best with our factory-trained technicians, genuine Audi parts, and state-of-the-art diagnostic equipment. From routine maintenance to complex repairs, we provide the expert care your Audi deserves.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiTool} className="w-6 h-6 text-red-500 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg uppercase tracking-tight">Audi-Certified Technicians</h3>
                    <p className="text-gray-400">Factory-trained specialists with ongoing Audi education</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiShield} className="w-6 h-6 text-red-500 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg uppercase tracking-tight">Genuine Audi Parts</h3>
                    <p className="text-gray-400">Authentic parts engineered specifically for your Audi</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-red-500 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg uppercase tracking-tight">Audi Care Plans</h3>
                    <p className="text-gray-400">Prepaid maintenance packages for predictable costs</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services" className="btn-audi-primary">
                  Schedule Service
                </Link>
                <Link to="/parts" className="btn-audi-outline">
                  Explore Parts
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <img src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800" alt="Audi service center" className="shadow-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Experience Audi in Halifax
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
              Visit Halifax Audi to explore our complete lineup, experience quattro AWD, and discover why Audi represents the future of luxury automotive engineering.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/build-your-audi" className="bg-white text-red-600 px-8 py-3 font-medium hover:bg-gray-100 transition-colors duration-200 uppercase tracking-wider text-sm">
                Build Your Audi
              </Link>
              <Link to="/contact" className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-red-600 transition-colors duration-200 uppercase tracking-wider text-sm">
                Schedule Test Drive
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;